/**
 * User Preferences API Client
 * 
 * Handles all API calls related to user preferences
 */

import apiService from './api';
import type { AppSettings } from '../contexts/SettingsContext';

export interface UserPreferences {
  id: number;
  user_id: string;
  appearance: {
    themeMode: 'light' | 'dark' | 'system';
    colorScheme: string;
  };
  spatial?: any;
  temporal?: any;
  routes?: any;
  visualization?: any;
  quality?: any;
  performance?: any;
  created_at: string;
  updated_at?: string;
}

/**
 * Get user preferences from the server
 */
export const getUserPreferences = async (): Promise<UserPreferences> => {
  return await apiService.getUserPreferences();
};

/**
 * Save user preferences to the server
 */
export const saveUserPreferences = async (settings: AppSettings): Promise<UserPreferences> => {
  const payload = {
    appearance: {
      themeMode: settings.appearance.themeMode,
      colorScheme: settings.appearance.colorScheme,
    },
    spatial: settings.spatial,
    temporal: settings.temporal,
    routes: settings.routes,
    visualization: settings.visualization,
    quality: settings.quality,
    performance: settings.performance,
  };

  return await apiService.saveUserPreferences(payload);
};

/**
 * Update user preferences on the server (partial update)
 */
export const updateUserPreferences = async (settings: Partial<AppSettings>): Promise<UserPreferences> => {
  const payload: any = {};

  if (settings.appearance) {
    payload.appearance = {
      themeMode: settings.appearance.themeMode,
      colorScheme: settings.appearance.colorScheme,
    };
  }

  if (settings.spatial) payload.spatial = settings.spatial;
  if (settings.temporal) payload.temporal = settings.temporal;
  if (settings.routes) payload.routes = settings.routes;
  if (settings.visualization) payload.visualization = settings.visualization;
  if (settings.quality) payload.quality = settings.quality;
  if (settings.performance) payload.performance = settings.performance;

  return await apiService.updateUserPreferences(payload);
};

/**
 * Delete user preferences (reset to defaults)
 */
export const deleteUserPreferences = async (): Promise<void> => {
  await apiService.deleteUserPreferences();
};

